import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar, Save, Link as LinkIcon } from "lucide-react";
import { safeValorForInput, safeDataForInput } from "@/utils";

export default function ContratoFormManual({ onSalvar, editais, isLoading }) {
  const [formData, setFormData] = useState({
    edital_id: "",
    numero_contrato: "",
    numero_processo: "",
    modalidade: "",
    orgao_contratante: "",
    unidade_gestora: "",
    fornecedor: "",
    cnpj_cpf: "",
    responsavel_interno: "",
    gestor_contrato: "",
    objeto: "",
    tipo_contrato: "",
    valor_total: "",
    valor_mensal_previsto: "",
    data_inicio: "",
    data_fim: "",
    periodicidade_reajuste: "",
    forma_pagamento: "",
    centro_custo: "",
    fonte_recurso: "",
    classificacao_orcamentaria: "",
    multas_penalidades: "",
    clausulas_principais: "",
    observacoes: "",
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (field === "edital_id" && value) {
      const edital = editais.find((e) => e.id === value);
      if (edital) {
        setFormData((prev) => ({
          ...prev,
          numero_processo: edital.numero_processo || "",
          orgao_contratante: edital.instituicao || "",
          objeto: edital.objeto || "",
          valor_total: safeValorForInput(edital.valor_estimado) || "",
          modalidade: edital.modalidade || "",
        }));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.numero_contrato || !formData.orgao_contratante || !formData.valor_total || !formData.data_inicio || !formData.data_fim) {
      alert("Preencha todos os campos obrigatórios!");
      return;
    }

    onSalvar({
      ...formData,
      valor_total: parseFloat(String(safeValorForInput(formData.valor_total))) || 0,
      valor_mensal_previsto: parseFloat(String(safeValorForInput(formData.valor_mensal_previsto))) || 0,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center gap-2 mb-3">
          <LinkIcon className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-blue-900">Vincular Licitação (Opcional)</h3>
        </div>
        <Select value={formData.edital_id || ""} onValueChange={(value) => handleChange("edital_id", value)}>
          <SelectTrigger>
            <SelectValue placeholder="Selecione uma licitação..." />
          </SelectTrigger>
          <SelectContent>
            {editais.filter(e => e.status_aprovacao === "aprovar_seguir").map((edital) => (
              <SelectItem key={edital.id} value={edital.id}>
                {edital.numero_pregao || edital.numero_processo} - {edital.instituicao}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900">Identificação</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="numero_contrato">Número do Contrato *</Label>
            <Input
              id="numero_contrato"
              value={formData.numero_contrato}
              onChange={(e) => handleChange("numero_contrato", e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="numero_processo">Número do Processo</Label>
            <Input
              id="numero_processo"
              value={formData.numero_processo}
              onChange={(e) => handleChange("numero_processo", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="modalidade">Modalidade</Label>
            <Input
              id="modalidade"
              value={formData.modalidade}
              onChange={(e) => handleChange("modalidade", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="tipo_contrato">Tipo de Contrato</Label>
            <Select value={formData.tipo_contrato || ""} onValueChange={(value) => handleChange("tipo_contrato", value)}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Serviço">Serviço</SelectItem>
                <SelectItem value="Fornecimento">Fornecimento</SelectItem>
                <SelectItem value="Obra">Obra</SelectItem>
                <SelectItem value="SaaS">SaaS</SelectItem>
                <SelectItem value="Locação">Locação</SelectItem>
                <SelectItem value="Consultoria">Consultoria</SelectItem>
                <SelectItem value="Manutenção">Manutenção</SelectItem>
                <SelectItem value="Outros">Outros</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900">Partes Contratantes</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="orgao_contratante">Órgão Contratante *</Label>
            <Input
              id="orgao_contratante"
              value={formData.orgao_contratante}
              onChange={(e) => handleChange("orgao_contratante", e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="unidade_gestora">Unidade Gestora</Label>
            <Input
              id="unidade_gestora"
              value={formData.unidade_gestora}
              onChange={(e) => handleChange("unidade_gestora", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="fornecedor">Fornecedor/Contratado</Label>
            <Input
              id="fornecedor"
              value={formData.fornecedor}
              onChange={(e) => handleChange("fornecedor", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="cnpj_cpf">CNPJ/CPF</Label>
            <Input
              id="cnpj_cpf"
              value={formData.cnpj_cpf}
              onChange={(e) => handleChange("cnpj_cpf", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="gestor_contrato">Gestor do Contrato</Label>
            <Input
              id="gestor_contrato"
              value={formData.gestor_contrato}
              onChange={(e) => handleChange("gestor_contrato", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="responsavel_interno">Responsável Interno</Label>
            <Input
              id="responsavel_interno"
              value={formData.responsavel_interno}
              onChange={(e) => handleChange("responsavel_interno", e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900">Objeto e Valores</h3>
        <div>
          <Label htmlFor="objeto">Objeto do Contrato</Label>
          <Textarea
            id="objeto"
            value={formData.objeto}
            onChange={(e) => handleChange("objeto", e.target.value)}
            rows={3}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="valor_total">Valor Total *</Label>
            <Input
              id="valor_total"
              type="number"
              step="0.01"
              value={safeValorForInput(formData.valor_total)}
              onChange={(e) => handleChange("valor_total", e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="valor_mensal_previsto">Valor Mensal Previsto</Label>
            <Input
              id="valor_mensal_previsto"
              type="number"
              step="0.01"
              value={safeValorForInput(formData.valor_mensal_previsto)}
              onChange={(e) => handleChange("valor_mensal_previsto", e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Vigência
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="data_inicio">Data de Início *</Label>
            <Input
              id="data_inicio"
              type="date"
              value={safeDataForInput(formData.data_inicio) || ""}
              onChange={(e) => handleChange("data_inicio", e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="data_fim">Data de Fim *</Label>
            <Input
              id="data_fim"
              type="date"
              value={safeDataForInput(formData.data_fim, true) || ""}
              onChange={(e) => handleChange("data_fim", e.target.value)}
              required
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900">Reajuste</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="indice_reajuste">Índice de Reajuste</Label>
            <Input
              id="indice_reajuste"
              placeholder="Ex: IPCA, IGP-M"
              value={formData.indice_reajuste}
              onChange={(e) => handleChange("indice_reajuste", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="periodicidade_reajuste">Periodicidade</Label>
            <Select value={formData.periodicidade_reajuste || ""} onValueChange={(value) => handleChange("periodicidade_reajuste", value)}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Mensal">Mensal</SelectItem>
                <SelectItem value="Trimestral">Trimestral</SelectItem>
                <SelectItem value="Semestral">Semestral</SelectItem>
                <SelectItem value="Anual">Anual</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900">Informações Financeiras</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="forma_pagamento">Forma de Pagamento</Label>
            <Input
              id="forma_pagamento"
              value={formData.forma_pagamento}
              onChange={(e) => handleChange("forma_pagamento", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="centro_custo">Centro de Custo</Label>
            <Input
              id="centro_custo"
              value={formData.centro_custo}
              onChange={(e) => handleChange("centro_custo", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="fonte_recurso">Fonte de Recurso</Label>
            <Input
              id="fonte_recurso"
              value={formData.fonte_recurso}
              onChange={(e) => handleChange("fonte_recurso", e.target.value)}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="classificacao_orcamentaria">Classificação Orçamentária</Label>
          <Input
            id="classificacao_orcamentaria"
            value={formData.classificacao_orcamentaria}
            onChange={(e) => handleChange("classificacao_orcamentaria", e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900">Cláusulas e Condições</h3>
        <div>
          <Label htmlFor="multas_penalidades">Multas e Penalidades</Label>
          <Textarea
            id="multas_penalidades"
            value={formData.multas_penalidades}
            onChange={(e) => handleChange("multas_penalidades", e.target.value)}
            rows={3}
          />
        </div>
        <div>
          <Label htmlFor="clausulas_principais">Cláusulas Principais</Label>
          <Textarea
            id="clausulas_principais"
            value={formData.clausulas_principais}
            onChange={(e) => handleChange("clausulas_principais", e.target.value)}
            rows={3}
          />
        </div>
        <div>
          <Label htmlFor="observacoes">Observações</Label>
          <Textarea
            id="observacoes"
            value={formData.observacoes}
            onChange={(e) => handleChange("observacoes", e.target.value)}
            rows={2}
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="submit" disabled={isLoading} className="bg-blue-600 hover:bg-blue-700">
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              Salvando...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Salvar Contrato
            </>
          )}
        </Button>
      </div>
    </form>
  );
}